export type BirthCalendar = 'solar' | 'lunar';
export type BirthGender = 'male' | 'female';
export type BirthTimeCertainty = 'exact' | 'approximate' | 'unknown_within_window';

export interface BirthInputDraft {
  calendar: BirthCalendar;
  date: string;
  timeIndex: number;
  gender: BirthGender;
  isLeapMonth?: boolean;
  certainty?: BirthTimeCertainty;
}

export interface ValidatedBirthInput extends BirthInputDraft {
  isLeapMonth: boolean;
  certainty: BirthTimeCertainty;
}

export interface BirthInputValidationIssue {
  field: 'calendar' | 'date' | 'timeIndex' | 'gender' | 'isLeapMonth';
  code: string;
  message: string;
}

export interface BirthInputValidationResult {
  ok: boolean;
  value?: ValidatedBirthInput;
  issues: readonly BirthInputValidationIssue[];
}

export const TIME_INDEX_META = Object.freeze([
  Object.freeze({ index: 0, label: '早子時', range: '00:00~01:00', branch: '子', lateRat: false }),
  Object.freeze({ index: 1, label: '丑時', range: '01:00~03:00', branch: '丑', lateRat: false }),
  Object.freeze({ index: 2, label: '寅時', range: '03:00~05:00', branch: '寅', lateRat: false }),
  Object.freeze({ index: 3, label: '卯時', range: '05:00~07:00', branch: '卯', lateRat: false }),
  Object.freeze({ index: 4, label: '辰時', range: '07:00~09:00', branch: '辰', lateRat: false }),
  Object.freeze({ index: 5, label: '巳時', range: '09:00~11:00', branch: '巳', lateRat: false }),
  Object.freeze({ index: 6, label: '午時', range: '11:00~13:00', branch: '午', lateRat: false }),
  Object.freeze({ index: 7, label: '未時', range: '13:00~15:00', branch: '未', lateRat: false }),
  Object.freeze({ index: 8, label: '申時', range: '15:00~17:00', branch: '申', lateRat: false }),
  Object.freeze({ index: 9, label: '酉時', range: '17:00~19:00', branch: '酉', lateRat: false }),
  Object.freeze({ index: 10, label: '戌時', range: '19:00~21:00', branch: '戌', lateRat: false }),
  Object.freeze({ index: 11, label: '亥時', range: '21:00~23:00', branch: '亥', lateRat: false }),
  Object.freeze({ index: 12, label: '晚子時', range: '23:00~00:00', branch: '子', lateRat: true }),
] as const);

function dateParts(value: string): { year: number; month: number; day: number } | undefined {
  const match = /^(\d{1,4})-(\d{1,2})-(\d{1,2})$/.exec(value.trim());
  if (!match) return undefined;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

function validSolarDate(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || year < 1 || !Number.isInteger(month) || month < 1 || month > 12 || !Number.isInteger(day) || day < 1) return false;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]!;
  return day <= daysInMonth;
}

function validLunarShape(year: number, month: number, day: number): boolean {
  return year >= 1 && month >= 1 && month <= 12 && day >= 1 && day <= 30;
}

export function timeIndexMeta(timeIndex: number) {
  return TIME_INDEX_META.find((item) => item.index === timeIndex);
}

export function validateBirthInput(input: BirthInputDraft): BirthInputValidationResult {
  const issues: BirthInputValidationIssue[] = [];
  if (input.calendar !== 'solar' && input.calendar !== 'lunar') {
    issues.push({ field: 'calendar', code: 'unsupported_calendar', message: '只支援陽曆或農曆。' });
  }

  const parts = dateParts(input.date);
  if (!parts) {
    issues.push({ field: 'date', code: 'invalid_date_format', message: '日期格式需為 YYYY-M-D。' });
  } else if (input.calendar === 'solar' && !validSolarDate(parts.year, parts.month, parts.day)) {
    issues.push({ field: 'date', code: 'invalid_solar_date', message: '陽曆日期不存在。' });
  } else if (input.calendar === 'lunar' && !validLunarShape(parts.year, parts.month, parts.day)) {
    issues.push({ field: 'date', code: 'invalid_lunar_date_shape', message: '農曆月份需為 1~12，日期需為 1~30。' });
  }

  if (!Number.isInteger(input.timeIndex) || input.timeIndex < 0 || input.timeIndex > 12) {
    issues.push({ field: 'timeIndex', code: 'invalid_time_index', message: '出生時辰索引需介於 0~12。' });
  }
  if (input.gender !== 'male' && input.gender !== 'female') {
    issues.push({ field: 'gender', code: 'invalid_gender', message: '性別值不符合目前排盤引擎需求。' });
  }
  if (input.calendar === 'solar' && input.isLeapMonth === true) {
    issues.push({ field: 'isLeapMonth', code: 'leap_month_only_for_lunar', message: '閏月只適用農曆輸入。' });
  }

  if (issues.length > 0) return { ok: false, issues };
  return {
    ok: true,
    value: {
      ...input,
      date: input.date.trim(),
      isLeapMonth: input.calendar === 'lunar' ? Boolean(input.isLeapMonth) : false,
      certainty: input.certainty ?? 'exact',
    },
    issues: [],
  };
}

export function shouldOfferBirthTimeComparison(input: ValidatedBirthInput): boolean {
  return input.certainty !== 'exact' || input.timeIndex === 0 || input.timeIndex === 12;
}
